import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  CAPABILITIES,
  DESK_IDENTIFY_REFUSAL_FR,
  deskOperatorLabel,
  type DeskOperatorCurrent,
  type DeskOperatorEntry,
  type DeskOperatorStore,
} from '@lemontaigu/platform';
import { useCapability } from './CapabilityProvider.js';

/**
 * « Qui êtes-vous ? » — the person at a shared desk login (DESK-OPERATOR-AND-
 * MAIL-BRIEF §2.1, ADR-0026). One component for pms.*, reception.* and bos.*,
 * mounted by each app's composition root with its own store.
 *
 * The header shows « Marie D. · Changer », or « Qui êtes-vous ? » when nobody
 * is identified. `ask()` opens the dialog and resolves true once someone has
 * identified. An audited write needs no call: when the database refuses it
 * (DK001, O3) the store asks through `onIdentifyRequired` and sends it again.
 *
 * ⚠ The database is the gate (desk_operator_resolve): the dialog only collects
 * the name and the PIN. Who is identified is re-read on focus and every minute,
 * so a session ended by 15 minutes idle shows as ended.
 */

interface DeskOperatorContextValue {
  current: DeskOperatorCurrent | null;
  /** « Marie D. », or null. */
  label: string | null;
  /** Opens « Qui êtes-vous ? »; true once someone is identified, false if dismissed. */
  ask: () => Promise<boolean>;
  /** « Changer »: ends the session and asks again. */
  change: () => Promise<void>;
  refresh: () => Promise<void>;
}

const DeskOperatorContext = createContext<DeskOperatorContextValue | null>(null);

export interface DeskOperatorProviderProps {
  /** Supplied by the app's composition root; never imported here. */
  store: DeskOperatorStore;
  children: ReactNode;
}

export function DeskOperatorProvider({ store, children }: DeskOperatorProviderProps) {
  const [current, setCurrent] = useState<DeskOperatorCurrent | null>(null);
  const [asking, setAsking] = useState(false);
  const waiters = useRef<((ok: boolean) => void)[]>([]);

  const refresh = useCallback(async () => {
    try {
      setCurrent(await store.current());
    } catch {
      setCurrent(null);
    }
  }, [store]);

  useEffect(() => {
    void refresh();
    const onFocus = () => void refresh();
    window.addEventListener('focus', onFocus);
    const t = window.setInterval(() => void refresh(), 60_000);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.clearInterval(t);
    };
  }, [refresh]);

  const settle = useCallback((ok: boolean) => {
    setAsking(false);
    const w = waiters.current;
    waiters.current = [];
    for (const resolve of w) resolve(ok);
  }, []);

  const ask = useCallback(
    () =>
      new Promise<boolean>((resolve) => {
        waiters.current.push(resolve);
        setAsking(true);
      }),
    [],
  );

  // O3: the database refused a write for want of a person — ask, and the
  // store's fetch sends the refused write again once someone has identified.
  useEffect(() => store.onIdentifyRequired(ask), [store, ask]);

  const value = useMemo<DeskOperatorContextValue>(
    () => ({
      current,
      label: current ? deskOperatorLabel(current) : null,
      ask,
      change: async () => {
        await store.end();
        setCurrent(null);
        setAsking(true);
      },
      refresh,
    }),
    [current, refresh, store, ask],
  );

  return (
    <DeskOperatorContext.Provider value={value}>
      {children}
      {asking && (
        <DeskOperatorDialog
          store={store}
          onDone={async () => {
            await refresh();
            settle(true);
          }}
          onCancel={() => settle(false)}
        />
      )}
    </DeskOperatorContext.Provider>
  );
}

/** The desk operator, or a no-op outside a provider (an app that has not mounted one). */
export function useDeskOperator(): DeskOperatorContextValue {
  const ctx = useContext(DeskOperatorContext);
  return (
    ctx ?? {
      current: null,
      label: null,
      ask: async () => true,
      change: async () => undefined,
      refresh: async () => undefined,
    }
  );
}

/** The header's chip: « Marie D. · Changer », or « Qui êtes-vous ? ». Shown to a
 *  login that writes bookings (the desk, the operator) — the same capability the
 *  database asks to list and identify; an inspector never sees it. */
export function DeskOperatorChip() {
  const ctx = useContext(DeskOperatorContext);
  const writes = useCapability(CAPABILITIES.PMS_RESERVATION_WRITE);
  if (!ctx || !writes) return null;
  if (!ctx.current) {
    return (
      <button type="button" className="desk-op-chip is-empty" onClick={() => void ctx.ask()}>
        Qui êtes-vous ?
      </button>
    );
  }
  return (
    <span className="desk-op-chip" aria-label="Personne à la réception">
      <span className="desk-op-name">{ctx.label}</span>
      {ctx.current.viaSession && (
        <button type="button" className="desk-op-change" onClick={() => void ctx.change()}>
          Changer
        </button>
      )}
    </span>
  );
}

const timeFr = (iso: string) => new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

function DeskOperatorDialog({ store, onDone, onCancel }: { store: DeskOperatorStore; onDone: () => void; onCancel: () => void }) {
  const [people, setPeople] = useState<readonly DeskOperatorEntry[] | null>(null);
  const [loadError, setLoadError] = useState('');
  const [picked, setPicked] = useState<DeskOperatorEntry | null>(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    store.list().then(setPeople, (e: unknown) => setLoadError(e instanceof Error ? e.message : String(e)));
  }, [store]);

  const submit = async () => {
    if (!picked || !/^\d{4,8}$/.test(pin)) return;
    setBusy(true);
    setError('');
    const r = await store.identify(picked.id, pin);
    setBusy(false);
    setPin('');
    if (r.ok) {
      onDone();
      return;
    }
    setError(
      r.reason === 'locked' && r.lockedUntil
        ? `${DESK_IDENTIFY_REFUSAL_FR.locked} Réessayez à ${timeFr(r.lockedUntil)}.`
        : DESK_IDENTIFY_REFUSAL_FR[r.reason],
    );
  };

  return (
    <div className="desk-op-backdrop" onClick={onCancel}>
      <div className="desk-op-dialog" role="dialog" aria-modal="true" aria-label="Qui êtes-vous ?" onClick={(e) => e.stopPropagation()}>
        <h2>Qui êtes-vous ?</h2>
        {loadError && <p className="auth-err">Liste indisponible : {loadError}</p>}
        {people && people.length === 0 && <p className="desk-op-hint">Personne n’est encore sur la liste de la réception (bos.* › RH › Liste de la réception).</p>}
        {!picked && people && (
          <div className="desk-op-people">
            {people.map((p) => (
              <button
                key={p.id}
                type="button"
                className="desk-op-person"
                disabled={!p.hasPin || p.lockedUntil !== null}
                title={!p.hasPin ? DESK_IDENTIFY_REFUSAL_FR.noPin : p.lockedUntil ? `Bloqué jusqu’à ${timeFr(p.lockedUntil)}` : undefined}
                onClick={() => setPicked(p)}
              >
                {deskOperatorLabel(p)}
                {!p.hasPin ? ' · pas de code' : p.lockedUntil ? ` · bloqué jusqu’à ${timeFr(p.lockedUntil)}` : ''}
              </button>
            ))}
          </div>
        )}
        {picked && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <p>
              <strong>{deskOperatorLabel(picked)}</strong>{' '}
              <button type="button" className="desk-op-link" onClick={() => { setPicked(null); setPin(''); setError(''); }}>
                Ce n’est pas moi
              </button>
            </p>
            <label className="desk-op-pin">
              Code (celui de la pointeuse)
              <input
                type="password"
                inputMode="numeric"
                autoComplete="off"
                pattern="[0-9]*"
                maxLength={8}
                value={pin}
                autoFocus
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              />
            </label>
            {error && <p className="auth-err">{error}</p>}
            <button type="submit" className="auth-btn" disabled={busy || pin.length < 4}>
              {busy ? 'Vérification…' : 'Valider'}
            </button>
          </form>
        )}
        <p>
          <button type="button" className="desk-op-link" onClick={onCancel}>
            Annuler
          </button>
        </p>
      </div>
    </div>
  );
}
